/**
 * Domain Dependencies Cache
 *
 * Performance-optimized cache for domain dependencies.
 * Similar to api-server's dependencyCache.
 *
 * Architecture:
 * - Caches dependencies per domain+orgId
 * - O(1) lookup time after first access
 * - Automatic cleanup on orgId change
 */

import type { DomainDependenciesMap } from "./domain-dependencies.types";

/**
 * Dependency cache
 * Key format: `${domain}:${orgId}`
 * Value: Domain dependencies object
 */
const dependencyCache = new Map<string, any>();

/**
 * Current orgId being used
 * Used to invalidate cache when orgId changes
 */
let currentOrgId: string | null = null;

/**
 * Get cache key for domain+orgId
 *
 * @param domain - Domain name
 * @param orgId - Organization ID
 * @returns Cache key
 */
export function getCacheKey(domain: string, orgId: string): string {
  return `${domain}:${orgId}`;
}

/**
 * Get cached dependencies
 *
 * @param domain - Domain name
 * @param orgId - Organization ID
 * @returns Cached dependencies or undefined
 */
export function getCachedDependencies<T = any>(
  domain: string,
  orgId: string,
): T | undefined {
  const key = getCacheKey(domain, orgId);
  return dependencyCache.get(key) as T | undefined;
}

/**
 * Set cached dependencies
 *
 * @param domain - Domain name
 * @param orgId - Organization ID
 * @param deps - Domain dependencies
 */
export function setCachedDependencies<T = any>(
  domain: string,
  orgId: string,
  deps: T,
): void {
  const key = getCacheKey(domain, orgId);
  dependencyCache.set(key, deps);
}

/**
 * Clear cache for a specific orgId
 *
 * @param orgId - Organization ID
 */
export function clearCacheForOrg(orgId: string): void {
  const keysToDelete: string[] = [];
  for (const key of dependencyCache.keys()) {
    if (key.endsWith(`:${orgId}`)) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach((key) => dependencyCache.delete(key));
}

/**
 * Clear all cache
 */
export function clearAllCache(): void {
  dependencyCache.clear();
}

/**
 * Update current orgId and clear cache if changed
 *
 * @param newOrgId - New organization ID
 */
export function updateOrgId(newOrgId: string | null): void {
  if (newOrgId !== currentOrgId) {
    if (currentOrgId) {
      clearCacheForOrg(currentOrgId);
    }
    currentOrgId = newOrgId;
  }
}

/**
 * Get cache size (for debugging)
 *
 * @returns Number of cached entries
 */
export function getCacheSize(): number {
  return dependencyCache.size;
}
